import { toMap } from '../toMap';

/**
 * Builds an array of names representing the full hierarchy path from root to the given item.
 * Uses an iterative approach with a Map lookup for O(depth) performance per item.
 *
 * @template T - Type that must have a name property (string)
 * @param {T} item - The item to get the hierarchy path for
 * @param {T[]} items - The complete flat array of items containing the hierarchy
 * @param {Object} keys - Configuration object specifying which properties to use for id and parent_id
 * @param {keyof T} keys.id - The property name used as the unique identifier
 * @param {keyof T} keys.parent_id - The property name used to reference the parent item
 * @returns {string[]} Array of names from root ancestor to the current item
 */
export const getHierarchyNames = <T extends { name: string }>(
  item: T,
  items: T[],
  keys: { id: keyof T; parent_id: keyof T },
): string[] => {
  const itemMap = toMap(items, keys.id);
  const names: string[] = [];
  let current: T | undefined = item;

  while (current) {
    names.unshift(current.name);
    const parentId = current[keys.parent_id];
    current = parentId ? itemMap.get(parentId) : undefined;
  }

  return names;
};
